<template>
  <div>
    <label v-if="label" class="block text-sm text-black mb-1" :class="labelClass || 'font-medium'">
      {{ label }}
      <span v-if="required" class="text-red-500">*</span>
    </label>

    <div :class="{ relative: showFieldErrorOverlay }">
      <!-- Input text/number/email/tel/date -->
      <input
          v-if="['text', 'number', 'email', 'tel', 'date', 'datetime-local', 'password'].includes(type)"
          :id="uniqueId"
          :type="type"
          :value="modelValue"
          :placeholder="placeholder"
          :disabled="disabled"
          :readonly="readonly"
          :min="min"
          :max="max"
          :step="step"
          :tabindex="skipTab ? -1 : undefined"
          @input="$emit('update:modelValue', ($event.target as HTMLInputElement).value)"
          @blur="onBlur"
          @wheel="handleWheel"
          :class="[

          'w-full h-9 px-3 text-sm text-black placeholder-gray-400 border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-colors',
          disabled ? 'bg-white cursor-not-allowed' : 'bg-white',
          readonly ? 'bg-gray-50' : '',
          invalidInputClass,
          customClass
        ]"
      />

      <!-- Select custom pour les parcours clavier -->
      <div v-else-if="type === 'select' && openOnFocus" class="relative">
        <button
          :id="uniqueId"
          ref="customSelectButtonRef"
          type="button"
          :disabled="disabled"
          :tabindex="skipTab ? -1 : undefined"
          class="w-full h-9 px-3 text-sm border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white transition-colors flex items-center justify-between gap-2 text-left"
          :class="[
            invalidInputClass,
            disabled ? 'cursor-not-allowed opacity-60' : '',
            customClass,
          ]"
          :aria-expanded="customSelectOpen"
          aria-haspopup="listbox"
          @pointerdown="customSelectPointerDown = true"
          @focus="handleCustomSelectFocus"
          @blur="handleCustomSelectBlur"
          @click="handleCustomSelectClick"
          @keydown.down.prevent="moveCustomSelectHighlight(1)"
          @keydown.up.prevent="moveCustomSelectHighlight(-1)"
          @keydown.enter.prevent="confirmCustomSelectHighlight"
          @keydown.space.prevent="confirmCustomSelectHighlight"
          @keydown.esc.prevent="customSelectOpen = false"
          @keydown.tab.prevent="handleCustomSelectTab"
        >
          <span
            class="truncate"
            :class="selectedOptionLabel ? 'text-black' : 'text-gray-400'"
          >
            {{ selectedOptionLabel || placeholder || "Sélectionner" }}
          </span>
          <i
            class="fas fa-chevron-down text-xs text-gray-400 transition-transform"
            :class="customSelectOpen ? 'rotate-180' : ''"
          ></i>
        </button>
        <div
          v-if="customSelectOpen"
          class="absolute left-0 right-0 top-full z-[300] mt-1 max-h-60 overflow-y-auto rounded-md border border-gray-300 bg-white shadow-lg"
          role="listbox"
        >
          <button
            v-if="placeholder"
            type="button"
            tabindex="-1"
            class="w-full px-3 py-2 text-left text-sm text-gray-500"
            :class="customSelectHighlightedIndex === -1 ? 'bg-blue-100' : 'hover:bg-gray-100'"
            @mousedown.prevent
            @click="selectCustomOption('')"
          >
            {{ placeholder }}
          </button>
          <button
            v-for="(option, index) in options"
            :key="`option-${option.value}`"
            type="button"
            tabindex="-1"
            class="w-full px-3 py-2 text-left text-sm text-black"
            :class="[
              index === customSelectHighlightedIndex
                ? 'bg-blue-100'
                : 'hover:bg-gray-100',
              String(option.value) === String(modelValue) ? 'font-semibold' : '',
            ]"
            role="option"
            :aria-selected="String(option.value) === String(modelValue)"
            @mousedown.prevent
            @mouseenter="customSelectHighlightedIndex = index"
            @click="selectCustomOption(option.value)"
          >
            {{ option.label }}
          </button>
        </div>
      </div>

      <!-- Select natif hors des parcours explicitement optimisés -->
      <select
        v-else-if="type === 'select'"
        :id="uniqueId"
        :value="modelValue"
        :disabled="disabled"
        :tabindex="skipTab ? -1 : undefined"
        @change="selectNativeOption"
        @blur="onBlur"
        :class="[
          'w-full h-9 px-3 text-sm text-black border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white transition-colors',
          invalidInputClass,
          customClass
        ]"
      >
        <option v-if="placeholder" value="">{{ placeholder }}</option>
        <option
          v-for="option in options"
          :key="`option-${option.value}`"
          :value="option.value"
        >
          {{ option.label }}
        </option>
      </select>

      <!-- Textarea -->
      <textarea
        v-else-if="type === 'textarea'"
        :id="uniqueId"
        :value="String(modelValue || '')"
        :placeholder="placeholder"
        :disabled="disabled"
        :readonly="readonly"
        :rows="rows"
        :tabindex="skipTab ? -1 : undefined"
        @input="handleTextareaInput"
        @blur="onBlur"
        :class="[
          'w-full px-3 py-2 text-sm text-black placeholder-gray-400 border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-none',
          disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white',
          readonly ? 'bg-gray-50' : '',
          invalidInputClass,
          customClass
        ]"
        :maxlength="props.maxLength"
      ></textarea>

      <!-- Checkbox -->
      <div v-else-if="type === 'checkbox'" class="flex items-center">
        <input
            :id="uniqueId"
            type="checkbox"
            :checked="Boolean(modelValue)"
            :disabled="disabled"
            :tabindex="skipTab ? -1 : undefined"
            @change="$emit('update:modelValue', ($event.target as HTMLInputElement).checked)"
            :class="[
            'w-4 h-4 rounded focus:ring-1 focus:ring-blue-500',
            error ? 'border-red-300 text-red-600' : 'border-gray-300 text-blue-600'
          ]"
        >
        <label :for="uniqueId" class="ml-2 text-sm text-black cursor-pointer">{{ placeholder }}</label>
      </div>

      <!-- Radio group -->
      <div v-else-if="type === 'radio'" class="space-y-1.5">
        <div
            v-for="option in options"
            :key="option.value"
            class="flex items-center"
        >
          <input
              :id="`${uniqueId}-${option.value}`"
              type="radio"
              :value="option.value"
              :checked="modelValue === option.value"
              :disabled="disabled"
              :tabindex="skipTab ? -1 : undefined"
              @change="$emit('update:modelValue', option.value)"
              :class="[
              'w-4 h-4 focus:ring-1 focus:ring-blue-500',
              error ? 'border-red-300 text-red-600' : 'border-gray-300 text-blue-600'
            ]"
          >
          <label :for="`${uniqueId}-${option.value}`" class="ml-2 text-sm text-black cursor-pointer">
            {{ option.label }}
          </label>
        </div>
      </div>

      <!-- Color picker -->
      <input
          v-else-if="type === 'color'"
          :id="uniqueId"
          type="color"
          :value="modelValue"
          :tabindex="skipTab ? -1 : undefined"
          @input="$emit('update:modelValue', ($event.target as HTMLInputElement).value)"
          class="w-full h-10 border border-gray-300 rounded-md cursor-pointer"
      />

      <!-- File input -->
      <input
          v-else-if="type === 'file'"
          :id="uniqueId"
          type="file"
          :accept="accept"
          :multiple="multiple"
          :tabindex="skipTab ? -1 : undefined"
          @change="$emit('update:modelValue', ($event.target as HTMLInputElement).files)"
          class="block w-full text-xs text-black file:mr-3 file:py-1.5 file:px-3 file:rounded-full file:border-0 file:text-xs file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
      />

      <p
        v-if="displayErrorMessage && showFieldErrorOverlay"
        class="absolute left-0 top-full z-[200] mt-0.5 text-xs text-red-600 bg-white border border-red-200 rounded px-1.5 py-0.5 shadow-md whitespace-nowrap pointer-events-none"
        role="alert"
      >
        {{ displayErrorMessage }}
      </p>
    </div>

    <!-- Compteur de caractères pour textarea -->
    <div v-if="type === 'textarea' && minLength" class="mt-1 text-xs" :class="getCharCountClass()">
      {{ String(modelValue || '').length }}/{{ minLength }} caractères
      <span v-if="String(modelValue || '').length >= minLength" class="text-green-600">✅</span>
      <span v-else-if="String(modelValue || '').length > 0" class="text-red-500">❌</span>
    </div>

    <!-- Help text -->
    <p v-if="helpText" class="mt-1 text-xs text-black">{{ helpText }}</p>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import type { BaseInputProps } from '@/types/ui';
import { focusRelativeElement } from '@/utils/focusNavigation'

const props = withDefaults(defineProps<BaseInputProps>(), {
  type: 'text',
  disabled: false,
  readonly: false,
  required: false,
  rows: 3,
  multiple: false,
  validateOnBlur: true,
  forceValidate: false,
  openOnFocus: false,
  skipTab: false,
});

const emit = defineEmits<{
  'update:modelValue': [value: string | number | boolean | FileList | null | undefined];
  'change': [value: string];
  'validation-changed': [isValid: boolean];
}>();

const uniqueId = computed(() => props.id || `input-${Math.random().toString(36).substr(2, 9)}`)
const touched = ref(false)
const customSelectButtonRef = ref<HTMLButtonElement | null>(null)
const customSelectOpen = ref(false)
const customSelectHighlightedIndex = ref(-1)
const customSelectPointerDown = ref(false)

const selectOptions = computed(() => props.options || [])
const selectedOptionLabel = computed(
  () =>
    selectOptions.value.find(
      (option) => String(option.value) === String(props.modelValue ?? ''),
    )?.label || '',
)

const showFieldErrorOverlay = computed(
  () => !['checkbox', 'radio'].includes(props.type),
)

const isEmptyValue = computed(() => {
  const v = props.modelValue
  if (v === null || v === undefined) return true
  if (typeof v === 'string') return v.trim() === ''
  if (typeof v === 'number') return Number.isNaN(v)
  if (typeof v === 'boolean') return false
  return false
})

const hasRequiredError = computed(
  () =>
    props.required &&
    isEmptyValue.value &&
    (touched.value || props.forceValidate),
)

const showErrorBorder = computed(() => !!props.error || hasRequiredError.value)

const displayErrorMessage = computed(() => {
  if (props.error) return props.error
  if (hasRequiredError.value) return 'Ce champ est obligatoire'
  return ''
})

const invalidInputClass = computed(() =>
  showErrorBorder.value
    ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
    : 'border-gray-300',
)

const onBlur = () => {
  if (props.validateOnBlur && props.required) {
    touched.value = true
  }
}

const openCustomSelect = () => {
  if (props.disabled) return
  const selectedIndex = selectOptions.value.findIndex(
    (option) => String(option.value) === String(props.modelValue ?? ''),
  )
  customSelectHighlightedIndex.value = selectedIndex
  customSelectOpen.value = true
}

const handleCustomSelectFocus = () => {
  if (!customSelectPointerDown.value) openCustomSelect()
}

const handleCustomSelectBlur = () => {
  customSelectPointerDown.value = false
  customSelectOpen.value = false
  onBlur()
}

const handleCustomSelectClick = () => {
  customSelectOpen.value = !customSelectOpen.value
  if (customSelectOpen.value) openCustomSelect()
  customSelectPointerDown.value = false
}

const moveCustomSelectHighlight = (delta: number) => {
  if (!customSelectOpen.value) openCustomSelect()
  const length = selectOptions.value.length
  if (!length) return
  if (customSelectHighlightedIndex.value < 0) {
    customSelectHighlightedIndex.value = delta > 0 ? 0 : length - 1
  } else {
    customSelectHighlightedIndex.value =
      (customSelectHighlightedIndex.value + delta + length) % length
  }
}

const selectCustomOption = (value: string) => {
  emit('update:modelValue', value)
  emit('change', value)
  customSelectOpen.value = false
}

const confirmCustomSelectHighlight = () => {
  if (!customSelectOpen.value) {
    openCustomSelect()
    return
  }
  const option =
    selectOptions.value[
      customSelectHighlightedIndex.value >= 0
        ? customSelectHighlightedIndex.value
        : 0
    ]
  if (option) selectCustomOption(option.value)
}

const handleCustomSelectTab = (event: KeyboardEvent) => {
  customSelectOpen.value = false
  void nextTick(() => {
    focusRelativeElement(customSelectButtonRef.value, event.shiftKey ? -1 : 1)
  })
}

const selectNativeOption = (event: Event) => {
  const value = (event.target as HTMLSelectElement).value
  emit('update:modelValue', value)
  emit('change', value)
}

watch(
  () => props.modelValue,
  () => {
    if (!isEmptyValue.value && touched.value) {
      // garde touched mais l'erreur disparaît via isEmptyValue
    }
  },
)

watch(
  () => props.forceValidate,
  (forced) => {
    if (forced && props.required) touched.value = true
  },
)

const handleTextareaInput = (event: Event) => {
  const value = (event.target as HTMLTextAreaElement).value
  emit('update:modelValue', value)

  if (props.minLength) {
    const isValid = value.length === 0 || value.length >= props.minLength
    emit('validation-changed', isValid)
  }
}

const handleWheel = (event: Event) => {
  if (props.type !== 'number') return
  ;(event.target as HTMLInputElement).blur()
}

const getCharCountClass = () => {
  const length = String(props.modelValue || '').length
  if (length >= (props.minLength || 0)) {
    return 'text-green-600'
  } else if (length > 0) {
    return 'text-red-500'
  }
  return 'text-black'
}
</script>

<style>
/* Hide number input spinners - using non-scoped style for pseudo-elements */
input[type="number"]::-webkit-outer-spin-button,
input[type="number"]::-webkit-inner-spin-button {
  -webkit-appearance: none !important;
  margin: 0;
}

input[type="number"] {
  -moz-appearance: textfield !important;
  appearance: textfield !important;
}
</style>
